import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {AuthService} from './auth.service';
import {Globals} from '../shared/globals';
import {TranslateService} from '@ngx-translate/core';
import {Station} from '../models/station.model';
import {StationDetailsUpdate} from '../models/station-details-update.model';
import {StationLocationUpdate} from '../models/station-location-update.model';
import {ConnectorDeltaUpdate} from '../models/connector-delta-update.model';
import {Api} from "../shared/api";
import { Image } from '../models/image.model';

@Injectable()
export class StationService {
  private getStationUrl: string;
  private patchStationUrl: string;
  private patchStationsUrl: string;
  private deleteStationLocationUrl: string;
  private patchConnectorsUrl: string;
  private patchConnectorsBulkUrl: string;
  private postImageUrl: (stationId: string) => string;
  private deleteImageUrl: (stationId: string, imageId: string) => string;
  public translate: TranslateService;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private globals: Globals,
    private api: Api,
    translate: TranslateService
  ) {
    this.getStationUrl = globals.apiUrl('users/stations');
    this.patchStationUrl = globals.apiUrl('users/stations/{stationId}');
    this.patchStationsUrl = globals.apiUrl('users/stations');
    this.patchConnectorsUrl = globals.apiUrl('users/stations/{stationId}/connectors');
    this.patchConnectorsBulkUrl = globals.apiUrl('users/stations/connectors');
    this.deleteStationLocationUrl = globals.apiUrl('users/stations/{stationId}/location');
    this.postImageUrl = (stationId: string) =>
      globals.apiUrl(`image?stationId=${stationId}`);
    this.deleteImageUrl = (stationId: string, imageId) =>
      globals.apiUrl(`image?stationId=${stationId}&imageId=${imageId}`);
    this.translate = translate;
  }

  getStations(): Promise<Station[]> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;

        return this.http
          .get(this.getStationUrl, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch((err) => {
        Promise.reject(err);
      });
  }

  async postImage(
    stationId: string,
    base64Data: string
  ): Promise<Image> {
    return (await this.api.httpPost(
      this.postImageUrl(stationId),
      base64Data
    )) as Image;
  }

  async deleteImage(
    stationId: string,
    imageId: string,
  ): Promise<void> {
    return await this.api.httpDelete(this.deleteImageUrl(stationId, imageId));
  }

  patchStationDetails(
    id: string,
    stationUpdate: StationDetailsUpdate
  ): Promise<any> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .patch(
            this.patchStationUrl.replace('{stationId}', id),
            stationUpdate,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: basicToken,
              },
            }
          )
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch((err) => Promise.reject(err));
  }

  patchStationLocation(
    id: string,
    stationUpdate: StationLocationUpdate
  ): Promise<any> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .patch(
            this.patchStationUrl.replace('{stationId}', id),
            stationUpdate,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: basicToken,
              },
            }
          )
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch((err) => Promise.reject(err));
  }

  deleteStationLocation(
    id: string,
  ): Promise<any> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .delete(
            this.deleteStationLocationUrl.replace('{stationId}', id),
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: basicToken,
              },
            }
          ).toPromise();
      });
  }

  patchStations(stationUpdates: StationDetailsUpdate[]): Promise<any> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .patch(this.patchStationsUrl, stationUpdates, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((err) => {
            return err;
          });
      })
      .catch((err) => Promise.reject(err));
  }

  patchConnectors(station: Station): Promise<any> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        const patchConnectors = new Array<ConnectorDeltaUpdate>();

        station.Connectors.forEach((connector) => {
          const patchConnector = new ConnectorDeltaUpdate();

          patchConnector.ID = connector.Id;
          patchConnector.Name = connector.Name;
          patchConnector.Description = connector.Description;
          patchConnector.EnergyPrice = connector.Price.EnergyPrice;
          patchConnector.ParkingPrice = connector.Price.ParkingPrice;
          patchConnector.AccessType = connector.AccessType;
          patchConnector.PlugType = connector.PlugType;
          patchConnector.MaxPower = connector.MaxPower;
          patchConnector.IsAutoChargeEnabled = connector.IsAutoChargeEnabled;
          patchConnector.IsHubjectEnabled = connector.Hubject.IsEnabled;
          patchConnector.HubjectPricingProductId =
            connector.Hubject.PricingProductId;

          patchConnectors.push(patchConnector);
        });

        return this.http
          .patch(
            this.patchConnectorsUrl.replace('{stationId}', station.ID),
            patchConnectors,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: basicToken,
              },
            }
          )
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch(this.handleError);
      })
      .catch((err) => Promise.reject(err));
  }

  patchConnectorsBulk(stations: Station[]): Promise<any> {
    return this.authService
      .getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        const patchConnectors = new Array<ConnectorDeltaUpdate>();

        stations.forEach((station) => {
          station.Connectors.forEach((connector) => {
            const patchConnector = new ConnectorDeltaUpdate();

            patchConnector.ID = connector.Id;
            patchConnector.StationId = station.ID;
            patchConnector.EnergyPrice = connector.Price.EnergyPrice;
            patchConnector.ParkingPrice = connector.Price.ParkingPrice;
            patchConnector.IsHubjectEnabled = connector.Hubject.IsEnabled;
            patchConnector.HubjectPricingProductId =
              connector.Hubject.PricingProductId;

            patchConnectors.push(patchConnector);
          });
        });

        return this.http
          .patch(this.patchConnectorsBulkUrl, patchConnectors, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch(this.handleError);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  private handleError(error: any): Promise<any> {
    // console.error('An error occurred', error); // for demo purposes only
    return Promise.reject(error);
  }
}
